import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { eventDays, todayInWarsaw } from '../events/calendar';
import { EVENTS_KEY, isClosed, type EventView } from '../events/labels';
import { FunctionSettingsEditor } from '../crew/ObsadaInfo';
import { personOptions } from '../crew/PositionsPanel';
import { mayTake, otherDaysText, placeName, releasedText, type ChangeResult, type CountResult, type FillResult, type OtherDaysResult, type PositionInfo, type PositionsView } from '../crew/positions';
import { refreshCrew } from '../crew/refreshCrew';
import { ASSIGNMENT_STATUSES, CREW_KEY, CREW_RESOURCE, dayShort, type Assignment, type AssignmentStatus } from '../crew/types';
import { useUnavailable } from '../crew/unavailable';
import { useCrewEvent } from '../crew/useCrewEvent';
import { PEOPLE_KEY, PERSONS_RESOURCE, type Person } from '../people/types';
import { DateQuestionDialog } from '../requests/DateQuestionDialog';
import { REQUESTS_RESOURCE, UNSENT_KEY } from '../requests/types';
import { DialogLayer, useEscapeLayer, useReturnFocus } from '../ui/Dialog';
import { confirmationLines, type Confirmation } from '../crew/confirmation';
import { ConfirmManualDialog } from './ConfirmManualDialog';
import { shortDate } from './peopleGrid';
import { Notice } from '../ui/Notice';
import { RowMenu, type RowAction } from '../ui/RowMenu';
import { CellHistory } from './CellHistory';
import { firstDay, panelGroups, personDays, toAsk, type PanelGroup, type PanelRow, type UnsentOne } from './eventPanel';
import { PEOPLE_AVAILABILITY_RESOURCE, PEOPLE_GRID_KEY, type Grid } from './peopleGrid';
import { crewLink, eventDates } from './staffing';
import { useSelfMarkAsk } from './SelfMarkDialog';
import '../crew/crew.css';

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

/**
 * Owner 2026-10-28 00:08 — variant A of the event panel of „Dostępność → Ludzie” (#201), „estetycznie, bez oczoplasu,
 * z potrzebnymi funkcjami” and without spoiling the grid: a drawer over the grid from the right (the grid keeps its
 * width); Esc or „×” closes it, the chosen cells stay chosen.
 * - Head: the name, one muted line „dates · place”, a day switch (several days), „⋯” with the rarer event actions
 *   („Wyślij potwierdzenie obsady (N)” — „Potwierdzeni” only, „Otwórz Obsadę”, ⚙ of the owner) and „×”.
 * - Body: one group per function in the ⚙ order with „N z M” (warning when places are missing) and a pencil that turns
 *   „M” into a small number field (Enter / leaving it saves, Esc cancels — the rules of Obsada's ⓘ Info). Then a row per
 *   place: the person (or „wolne”), the status as a dot and one word, at most one action („Poproś” / „Ponów” to THIS
 *   person — the recipient listed before sending; „Obsadź” — the place's picker). Rarer actions in the row's „⋯”.
 * - Footer: „Poproś niepotwierdzonych (N)” — every „Proponowany” not asked yet, one question each with their days.
 * Nothing is sent by itself; every change goes through `refreshCrew`. The quote's green bar stays at the bottom (#205).
 */
export function EventPanel({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const event = useQuery({ queryKey: [...EVENTS_KEY, 'one', eventId], queryFn: () => api<EventView>(`/api/events/${eventId}`) });
  // the base layer: a menu or a dialog over the panel closes first
  useEscapeLayer(() => onClose(), true, { base: true });
  // review M3: closed — the focus goes back where it was (the bar or the cell that opened it)
  useReturnFocus(true);
  const e = event.data;
  const name = e?.name || 'Wydarzenie';
  const box = useVisibleTop();
  return (
    <aside ref={box} className="pg-side ep" aria-label={`Wydarzenie: ${e ? name : ''}`.trim()} data-feature-id="crew.grid_staffing">
      <div className="ep-frame">
        {e ? (
          <Body event={e} onClose={onClose} />
        ) : (
          <div className="ep-head">
            <div className="ep-title">
              <h2>{event.error ? 'Wydarzenie' : 'Wczytywanie…'}</h2>
            </div>
            <CloseButton onClose={onClose} />
            {event.error && (
              <p className="alert" role="alert">
                {event.error instanceof ApiError ? event.error.message : 'Nie udało się wczytać wydarzenia'}
              </p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * The drawer spans the grid area; its frame (head, rows, footer) sticks to the window and is never taller than what is
 * visible of it — so the head and the footer are always in sight (`--ep-top`: where the drawer starts in the window).
 */
function useVisibleTop() {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      el.style.setProperty('--ep-top', `${Math.max(0, Math.round(el.getBoundingClientRect().top))}px`);
    };
    const later = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', later, { passive: true, capture: true });
    window.addEventListener('resize', later);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', later, { capture: true });
      window.removeEventListener('resize', later);
    };
  }, []);
  return ref;
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button className="ep-close" type="button" aria-label="Zamknij panel" title="Zamknij (Esc)" onClick={onClose}>
      ×
    </button>
  );
}

type Asking = Array<{ personId: string; name?: string | undefined; days: string[] }>;

function Body({ event, onClose }: { event: EventView; onClose: () => void }) {
  const { me } = useMe();
  const navigate = useNavigate();
  const crew = useCrewEvent(event);
  const today = todayInWarsaw();
  const view = useQuery({ queryKey: [...CREW_KEY, 'positions', event.id], queryFn: () => api<PositionsView>(`/api/crew/events/${event.id}/positions`) });
  const info = useQuery({ queryKey: [...CREW_KEY, 'position-info', event.id], queryFn: () => api<PositionInfo>(`/api/crew/events/${event.id}/position-info`) });
  const mayCounts = !!me && can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'functionId');
  const mayStatus = !!me && can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'status');
  const mayFill = !!me && can(me, CREW_RESOURCE, 'create') && fieldVisible(me, CREW_RESOURCE, 'personId') && fieldVisible(me, CREW_RESOURCE, 'functionId') && can(me, PERSONS_RESOURCE, 'view');
  const mayRelease = !!me && can(me, CREW_RESOURCE, 'delete') && fieldVisible(me, CREW_RESOURCE, 'personId');
  // as Obsada's „Zmień” (#198): fill and remove
  const mayChange = mayFill && !!me && can(me, CREW_RESOURCE, 'delete');
  const mayHistory = !!me && can(me, PEOPLE_AVAILABILITY_RESOURCE, 'view');
  // a question pinned to the event: „Zapytania o dostępność: Dodawanie” and „Obsada: Dodawanie” (as „Do wysłania”);
  // review I3: and „Zapytania o dostępność: Podgląd” — who was asked is what decides „Poproś”
  const maySend = !!me && can(me, REQUESTS_RESOURCE, 'create') && can(me, REQUESTS_RESOURCE, 'view') && can(me, CREW_RESOURCE, 'create');
  // review I2: „Poproś” and the footer — the server's „Do wysłania” of this event (its rule, rights and rule 7a); the
  // same query as the counter over the grid, read again by every crew change and send
  const unsentQuery = useQuery({ queryKey: UNSENT_KEY, enabled: !!me && can(me, CREW_RESOURCE, 'view'), queryFn: () => api<{ events?: Array<{ eventId: string; people?: UnsentOne[] }> }>('/api/requests/unsent') });
  const unsent = unsentQuery.data?.events?.find((x) => x.eventId === event.id)?.people ?? (unsentQuery.data ? [] : undefined);
  const closed = isClosed(event.status);
  const heading = useRef<HTMLHeadingElement>(null);
  // review M3: the panel opened — the focus goes in (its heading)
  const ready = !!me;
  useEffect(() => {
    if (ready) heading.current?.focus({ preventScroll: true });
  }, [ready]);
  // „dostępny”: the grid's marks of the crew on the event's days
  const days = view.data?.days.map((d) => d.day) ?? [];
  const people = [...new Set((view.data?.days ?? []).flatMap((d) => d.groups.flatMap((g) => g.places.flatMap((p) => (p.personId ? [p.personId] : [])))))].sort();
  const marksQuery = days.length > 0 && people.length > 0 ? `from=${days[0]}&to=${days.at(-1)}&personIds=${people.join(',')}` : '';
  const marks = useQuery({ queryKey: [...PEOPLE_GRID_KEY, 'panel', event.id, marksQuery], enabled: mayHistory && marksQuery !== '', queryFn: () => api<Grid>(`/api/crew/people-availability?${marksQuery}`) });
  const available = new Map<string, Set<string>>();
  for (const c of marks.data?.cells ?? []) if (c.state === 'available' && !c.startsAt) available.set(c.personId, (available.get(c.personId) ?? new Set()).add(c.day));

  const [chosenDay, setDay] = useState<string | null>(null);
  const [gear, setGear] = useState(false);
  const [asking, setAsking] = useState<Asking | null>(null);
  const [history, setHistory] = useState<{ personId: string; name: string; day: string } | null>(null);
  const actions = usePlaceActions(event, () => void crew.crewQuote.ask());
  if (!me) return null;

  const name = event.name || 'Wydarzenie';
  const span = event.startsAt && event.endsAt ? eventDays({ startsAt: event.startsAt, endsAt: event.endsAt }) : null;
  const when = [span ? eventDates({ firstDay: span.first, lastDay: span.last }) : '', event.location ?? ''].filter(Boolean).join(' · ');
  const day = chosenDay && days.includes(chosenDay) ? chosenDay : firstDay(days, today);
  const input = { assignments: crew.list, asked: crew.asked, available, unsent, closed, today, rights: { send: maySend, fill: mayFill } };
  const groups = view.data ? panelGroups({ ...input, view: view.data, info: info.data, day }) : [];
  const staffed = groups.filter((g) => g.needed > 0 || g.rows.length > 0);
  const spare = groups.filter((g) => g.needed === 0 && g.rows.length === 0);
  const unasked = toAsk({ unsent, closed, rights: input.rights });
  const confirmed = new Set(crew.list.filter(crew.goes).map((a) => a.personId ?? a.id)).size;

  const headActions: RowAction[] = [
    // review M5: without the field „Status” nobody's status is known — no count
    ...(crew.maySend ? [{ label: fieldVisible(me, CREW_RESOURCE, 'status') ? `Wyślij potwierdzenie obsady (${confirmed})` : 'Wyślij potwierdzenie obsady', disabled: !!crew.confirmBlocked, title: crew.confirmBlocked ?? undefined, onSelect: () => crew.setConfirming(true) }] : []),
    { label: 'Otwórz Obsadę', onSelect: () => navigate(crewLink(event.id)) },
    ...(me.isOwner ? [{ label: 'Funkcje w panelu ⚙', onSelect: () => setGear(!gear) }] : []),
  ];

  const ask = (r: PanelRow) => {
    if (!r.personId) return;
    // „Poproś”: the days the server would ask about („Do wysłania”); „Ponów”: the person's days (also beyond the number)
    const days = (r.action === 'ask' ? unsent?.find((u) => u.personId === r.personId)?.days : undefined) ?? (view.data ? personDays(view.data, r.personId, crew.list, today) : []);
    setAsking([{ personId: r.personId, name: r.place.personName ?? r.assignment?.personName, days }]);
  };

  const renderGroup = (g: PanelGroup) => (
    <FunctionGroup
      key={`${day}:${g.key}`}
      group={g}
      eventId={event.id}
      day={day}
      multiDay={!!view.data?.multiDay}
      // review I4: rule B — no pencil on a past day
      editable={mayCounts && day >= today}
      onCounted={() => void crew.crewQuote.ask()}
      row={(r) => (
        <Row
          key={r.key}
          row={r}
          multiDay={!!view.data?.multiDay}
          picking={actions.picking?.key === r.key ? actions.picking.mode : null}
          actions={actions}
          onAsk={() => ask(r)}
          more={[
            // owner („potwierdzenia telefoniczne”): the status by hand, as Obsada's select; rule B — not on a past day
            ...(mayStatus && r.assignment?.status && r.day >= today ? [{ label: 'Zmień status…', onSelect: () => actions.pick(r.key, 'status') }] : []),
            // owner 2026-10-28 12:01 „Potwierdź ręcznie”: straight to „Potwierdzony”, with an optional note (rule B as above)
            ...(mayStatus && r.assignment?.status === 'proponowany' && r.day >= today ? [{ label: 'Potwierdź ręcznie', onSelect: () => actions.confirmManually(r.assignment!, r.name, r.day, r.key) }] : []),
            // #198 „Zmień osobę” (owner: „muszę móc edytować raz wybraną osobę”)
            ...(mayChange && !r.free && r.personId && (r.place.id || r.place.assignmentId) && r.day >= today ? [{ label: 'Zmień osobę', onSelect: () => actions.pick(r.key, 'person') }] : []),
            ...(!r.extra && mayRelease && r.assignment && r.personId && r.day >= today ? [{ label: 'Zdejmij', title: 'Zdejmij osobę z miejsca — miejsce zostaje wolne', onSelect: () => actions.release(r, view.data!) }] : []),
            ...(r.assignment && r.personId && crew.mayDelete ? [{ label: 'Usuń z obsady', danger: true, onSelect: () => crew.askRemove(r.assignment!) }] : []),
            ...(mayStatus && r.mayForce && r.assignment ? [{ label: 'Wymuś', title: 'Osoba robi to wydarzenie w tym dniu; w drugim będzie nieaktywna', onSelect: () => actions.force(r.assignment!, r.day) }] : []),
            ...(mayHistory && r.personId ? [{ label: 'Historia', onSelect: () => setHistory({ personId: r.personId!, name: r.name, day: r.day }) }] : []),
          ]}
        />
      )}
    />
  );

  return (
    <>
      <header className="ep-head">
        <div className="ep-title">
          <h2 ref={heading} tabIndex={-1}>
            {name}
          </h2>
          {when && <p className="ep-when">{when}</p>}
        </div>
        <RowMenu label={`Więcej działań: ${name}`} actions={headActions} floating="end" />
        <CloseButton onClose={onClose} />
        {view.data?.multiDay && days.length > 1 && (
          <div className="ep-days" role="group" aria-label="Dzień">
            {days.map((d) => (
              <button key={d} type="button" className={`ep-day${d === day ? ' on' : ''}${d < today ? ' past' : ''}`} aria-pressed={d === day} onClick={() => setDay(d)}>
                {dayShort(d)}
              </button>
            ))}
          </div>
        )}
      </header>
      {gear && me.isOwner && (
        <div className="ep-gear">
          <FunctionSettingsEditor />
        </div>
      )}
      <div className="ep-body">
        {view.error && (
          <p className="alert" role="alert">
            {errorText(view.error, 'Nie udało się wczytać obsady')}
          </p>
        )}
        {actions.error && (
          <p className="alert" role="alert">
            {actions.error}
          </p>
        )}
        {view.data && staffed.length === 0 && <p className="muted ep-empty">Żadna funkcja nie ma jeszcze miejsc.{mayCounts && spare.length > 0 ? ' Liczbę ustawisz ołówkiem w „Bez miejsc” poniżej.' : ''}</p>}
        {staffed.map(renderGroup)}
        {mayCounts && day >= today && spare.length > 0 && (
          // functions of ⚙ with no places: one quiet list at the bottom — the pencil adds places
          <section className="ep-spare" aria-label="Funkcje bez miejsc">
            <h3 className="ep-spare-title">Bez miejsc</h3>
            {spare.map(renderGroup)}
          </section>
        )}
        {history && <CellHistory key={`${history.personId}:${history.day}`} personId={history.personId} personName={history.name} day={history.day} onClose={() => setHistory(null)} />}
      </div>
      {unasked.length > 0 && (
        <footer className="ep-foot">
          <button className="btn" type="button" data-feature-id="requests.dates" onClick={() => setAsking(unasked)}>
            Poproś niepotwierdzonych ({unasked.length})
          </button>
        </footer>
      )}
      {/* the dialogs go to the page itself: the drawer is a layer of its own (z-index) and must not hold them under the menu */}
      {createPortal(
        <>
          {asking && (
            <DateQuestionDialog
              title="Poproś o potwierdzenie"
              perPerson={asking}
              event={{ id: event.id, name }}
              inCrew
          initialMessage={`Proszę o potwierdzenie: czy możesz pracować przy wydarzeniu „${name}” w te dni?`}
              onClose={() => setAsking(null)}
            />
          )}
          {actions.dialogs}
          {crew.dialogs}
        </>,
        document.body,
      )}
      {actions.notice && <Notice onClose={actions.clearNotice}>{actions.notice}</Notice>}
      {crew.crewQuote.notice}
    </>
  );
}

/** A function's group: a thin bar with the name and „N z M” (✎ — the number), then its rows. */
function FunctionGroup({ group: g, eventId, day, multiDay, editable, onCounted, row }: { group: PanelGroup; eventId: string; day: string; multiDay: boolean; editable: boolean; onCounted: () => void; row: (r: PanelRow) => React.ReactNode }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [released, setReleased] = useState<string | null>(null);
  const setCount = useMutation({
    mutationFn: (count: number) => api<CountResult>(`/api/crew/events/${eventId}/position-counts`, { method: 'PUT', body: { functionId: g.others ? null : g.functionId, count, ...(multiDay ? { day } : {}) } }),
    onSuccess: async (r) => {
      setReleased(releasedText(r.released));
      await refreshCrew(queryClient);
      onCounted();
    },
  });
  const start = () => {
    setText(String(g.needed));
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const t = text.trim();
    if (!/^\d+$/.test(t) || Number(t) === g.needed) return;
    setCount.mutate(Number(t));
  };
  const short = g.filled < g.needed;
  return (
    <section className="ep-group" role="group" aria-label={g.functionName}>
      <div className="ep-group-head">
        <span className="ep-fn">{g.functionName}</span>
        <span className={`ep-count${short ? ' short' : ''}`}>
          {g.filled} z{' '}
          {editing ? (
            <input
              className="ep-count-input"
              type="text"
              inputMode="numeric"
              aria-label={`Liczba miejsc: ${g.functionName}`}
              value={text}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setText(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit();
                } else if (e.key === 'Escape') {
                  // the field's own Escape: the panel stays open
                  e.preventDefault();
                  setEditing(false);
                }
              }}
            />
          ) : (
            g.needed
          )}
        </span>
        {editable && !editing && (
          <button className="ep-pencil" type="button" aria-label={`Zmień liczbę miejsc: ${g.functionName}`} title="Zmień liczbę miejsc" disabled={setCount.isPending} onClick={start}>
            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M11.2 2.3l2.5 2.5-8.2 8.2-3 .5.5-3 8.2-8.2z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>
      {setCount.error && (
        <p className="alert ep-group-error" role="alert">
          {errorText(setCount.error, 'Nie udało się zmienić liczby')}
        </p>
      )}
      {released && <Notice onClose={() => setReleased(null)}>{released}</Notice>}
      {g.rows.length > 0 && <ul className="ep-rows">{g.rows.map(row)}</ul>}
    </section>
  );
}

function Row({ row: r, multiDay, picking, actions, onAsk, more }: { row: PanelRow; multiDay: boolean; picking: PickMode | null; actions: PlaceActions; onAsk: () => void; more: RowAction[] }) {
  const label = r.extra ? `${r.group.functionName} — poza liczbą (${r.name})` : r.place.label;
  const accessible = placeName(r.day, label, multiDay);
  return (
    <li className={`ep-row${r.free ? ' free' : ''}`} aria-label={accessible} data-row={r.key}>
      <span className="ep-who">{picking === 'fill' ? actions.picker(r, accessible) : picking === 'person' ? actions.personPicker(r, accessible) : r.name}</span>
      <span className="ep-status">
        {picking === 'status' && r.assignment ? (
          actions.statusPicker(r.assignment, r.key)
        ) : r.status && (
          <>
            <i className="ep-dot" data-tone={r.status.tone} aria-hidden="true" />
            <span className="ep-word" data-tone={r.status.tone}>
              {r.status.word}
            </span>
          </>
        )}
        {r.collision && picking !== 'status' && <i className="ep-coll" role="img" aria-label={r.collision} title={r.collision} />}
        {/* owner 2026-10-28 12:01: how a „potwierdzony” was confirmed (rule 7a: no status — no word, no ⓘ) */}
        {picking !== 'status' && r.assignment?.status === 'potwierdzony' && r.status?.word === 'potwierdzony' && <ConfirmInfo name={r.name} confirmation={r.assignment.confirmation} />}
      </span>
      <span className="ep-act">
        {r.action === 'ask' && (
          <button className="ep-link" type="button" data-feature-id="requests.dates" aria-label={`Poproś: ${r.name}`} title="Poproś tę osobę o potwierdzenie jej dni" onClick={onAsk}>
            Poproś
          </button>
        )}
        {r.action === 'reask' && (
          <button className="ep-link" type="button" data-feature-id="requests.dates" aria-label={`Ponów: ${r.name}`} title="Wyślij to zapytanie jeszcze raz" onClick={onAsk}>
            Ponów
          </button>
        )}
        {r.action === 'fill' && !picking && (
          <button className="ep-link" type="button" aria-label={`Obsadź: ${accessible}`} onClick={() => actions.pick(r.key, 'fill')}>
            Obsadź
          </button>
        )}
      </span>
      <span className="ep-more">
        <RowMenu label={`Więcej: ${r.name}`} actions={more} floating="end" />
      </span>
      {actions.rowError?.key === r.key && (
        <span className="ep-row-error" role="alert">
          {actions.rowError.text}
        </span>
      )}
    </li>
  );
}

type PlaceActions = ReturnType<typeof usePlaceActions>;
/** What a row shows in place of its text: the picker of a free place, „Zmień osobę”, „Zmień status…”. */
type PickMode = 'fill' | 'person' | 'status';

/**
 * The panel's own crew changes — the same endpoints and rules as Obsada's places: the person picker of a free place
 * (only people with the function, unavailable greyed out; the rules' place joins as „Dodaj osobę”), „Obsadzić tę osobę
 * także w pozostałych dniach?”, „Zmień osobę” (#198), „Zmień status…” (Obsada's options — the owner's phone
 * confirmations), „Zdejmij” (the last day asks first) and „Wymuś”. Every change: `refreshCrew`, then
 * the quote's question (`onChanged`).
 */
function usePlaceActions(event: EventView, onChanged: () => void) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const mayFill = !!me && can(me, CREW_RESOURCE, 'create') && fieldVisible(me, CREW_RESOURCE, 'personId') && fieldVisible(me, CREW_RESOURCE, 'functionId') && can(me, PERSONS_RESOURCE, 'view');
  const persons = useQuery({ queryKey: [...PEOPLE_KEY, 'persons', 'crew-candidates', ''], enabled: mayFill, queryFn: () => api<{ persons: Person[]; truncated?: boolean }>('/api/people/persons') });
  const unavailable = useUnavailable(event.id, mayFill);
  const [picking, setPickingState] = useState<{ key: string; mode: PickMode } | null>(null);
  // review M3: a row's picker closed — the focus goes to that row's „⋯” (else its action), not to the page
  const refocus = useRef<string | null>(null);
  const setPicking = (next: { key: string; mode: PickMode } | null) => {
    if (!next && picking) refocus.current = picking.key;
    setPickingState(next);
  };
  useEffect(() => {
    const key = refocus.current;
    if (!key || picking) return;
    refocus.current = null;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const row = document.querySelector(`.ep-row[data-row="${key}"]`);
    (row?.querySelector<HTMLElement>('.ep-more button') ?? row?.querySelector<HTMLElement>('.ep-act button'))?.focus();
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ key: string; text: string } | null>(null);
  const [askDays, setAskDays] = useState<{ positionId: string; personName: string; days: string[] } | null>(null);
  const [lastDay, setLastDay] = useState<{ assignmentId: string; day: string; personName: string } | null>(null);
  const refresh = () => refreshCrew(queryClient);
  const changed = async () => {
    await refresh();
    onChanged();
  };
  const collisions = (c: OtherDaysResult['collisions'] | undefined) => (c && c.length > 0 ? c.map((x) => `Kolizja ${dayShort(x.day)}: także „${x.events.map((e) => e.eventName).join('”, „')}”.`).join(' ') : null);
  const refused = (key: string) => (e: unknown) => setRowError({ key, text: errorText(e, 'Nie udało się obsadzić') });
  const fill = useMutation({
    mutationFn: (b: { placeId: string; personId: string; personName: string; key: string }) => api<FillResult & { collisions?: OtherDaysResult['collisions'] }>(`/api/crew/positions/${b.placeId}/fill`, { method: 'POST', body: { personId: b.personId } }),
    onMutate: () => setRowError(null),
    onSuccess: async (r, b) => {
      setPicking(null);
      setNotice(collisions(r.collisions));
      await refresh();
      if (r.positionId && r.otherDays.length > 0) setAskDays({ positionId: r.positionId, personName: b.personName, days: r.otherDays });
      else onChanged();
    },
    onError: (e, b) => refused(b.key)(e),
  });
  // #198 „Zmień osobę”: a place with a row, or the assignment behind a place of the rules / outside the places
  const change = useMutation({
    mutationFn: async (b: { place: PanelRow['place']; version: number | undefined; personId: string; personName: string; key: string }) => {
      if (b.place.id) return api<ChangeResult>(`/api/crew/positions/${b.place.id}/change`, { method: 'POST', body: { personId: b.personId } });
      // review M7: never a made-up version — the assignment not loaded yet: read the crew first
      const version = b.version ?? (await api<{ assignments: Assignment[] }>(`/api/crew/events/${event.id}/assignments`)).assignments.find((a) => a.id === b.place.assignmentId)?.version;
      if (version === undefined) throw new Error('Nie udało się wczytać przydziału — odśwież i spróbuj ponownie');
      return api<ChangeResult>(`/api/crew/assignments/${b.place.assignmentId}/change-person`, { method: 'POST', body: { personId: b.personId, version } });
    },
    onMutate: () => setRowError(null),
    onSuccess: async (r, b) => {
      setPicking(null);
      setNotice(collisions(r.collisions));
      await refresh();
      if (r.positionId && r.otherDays.length > 0) setAskDays({ positionId: r.positionId, personName: b.personName, days: r.otherDays });
      else onChanged();
    },
    onError: (e, b) => refused(b.key)(e),
  });
  // owner („potwierdzenia telefoniczne”): the status by hand — Obsada's PATCH
  // review I2: „Odmowa” over the person's own mark — „Wpis pracownika” first, then the same with the token
  const selfAsk = useSelfMarkAsk();
  const changeStatus = useMutation({
    mutationFn: (b: { a: Assignment; status: AssignmentStatus; key: string; consulted?: string }) =>
      api<Assignment>(`/api/crew/assignments/${b.a.id}`, { method: 'PATCH', body: { version: b.a.version, status: b.status, ...(b.consulted ? { consulted: b.consulted } : {}) } }),
    onMutate: () => setRowError(null),
    onSuccess: async () => {
      setPicking(null);
      await changed();
    },
    onError: async (e, b) => {
      setPicking(null);
      if (selfAsk.offer(e, (consulted) => changeStatus.mutate({ ...b, consulted }))) return;
      setRowError({ key: b.key, text: errorText(e, 'Nie udało się zmienić statusu') });
      // review M4: changed meanwhile — read the crew again (the row shows the status it has now)
      if (e instanceof ApiError && e.status === 409) await refresh();
    },
  });
  // owner 2026-10-28 12:01 „Potwierdź ręcznie”: Obsada's PATCH to „Potwierdzony” with the note (recorded with who and when)
  const [confirmFor, setConfirmFor] = useState<{ a: Assignment; name: string; day: string; key: string } | null>(null);
  const confirmManually = useMutation({
    mutationFn: (b: { a: Assignment; note: string; key: string }) => api<Assignment>(`/api/crew/assignments/${b.a.id}`, { method: 'PATCH', body: { version: b.a.version, status: 'potwierdzony', confirmNote: b.note } }),
    onSuccess: async () => {
      setConfirmFor(null);
      await changed();
    },
    onError: async (e, b) => {
      // review 5: changed meanwhile — the step closes (no retry with a stale version), the crew is read again, the row says why
      if (e instanceof ApiError && e.status === 409) {
        setConfirmFor(null);
        setRowError({ key: b.key, text: errorText(e, 'Ktoś zmienił ten przydział — odśwież') });
        await refresh();
      }
    },
  });
  const fillRule = useMutation({
    mutationFn: (b: { personId: string; functionId: string; key: string }) => api(`/api/crew/events/${event.id}/assignments`, { method: 'POST', body: { personId: b.personId, functionId: b.functionId } }),
    onMutate: () => setRowError(null),
    onSuccess: async () => {
      setPicking(null);
      await changed();
    },
    onError: (e, b) => refused(b.key)(e),
  });
  const fillOthers = useMutation({
    mutationFn: (positionId: string) => api<OtherDaysResult>(`/api/crew/positions/${positionId}/fill-other-days`, { method: 'POST', body: {} }),
    onSuccess: async (r) => {
      setAskDays(null);
      setNotice(otherDaysText(r));
      await changed();
    },
  });
  const releasePerson = useMutation({
    mutationFn: (b: { assignmentId: string; day: string }) => api<CountResult>(`/api/crew/assignments/${b.assignmentId}/release`, { method: 'POST', body: { day: b.day } }),
    onSuccess: async (r) => {
      setNotice(releasedText(r.released));
      await changed();
    },
  });
  const forceDay = useMutation({
    mutationFn: (b: { a: Assignment; day: string }) => api(`/api/crew/assignments/${b.a.id}/force`, { method: 'POST', body: { days: [b.day] } }),
    onSuccess: changed,
  });
  const busy = fill.isPending || fillRule.isPending || change.isPending;
  /** Esc on a row's control: back to the row's text (the panel stays open). */
  const escape = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    setPicking(null);
  };
  const people = persons.data?.persons ?? [];
  const error = [releasePerson.error, forceDay.error].find(Boolean);

  const picker = (r: PanelRow, label: string) => (
    <select
      className="ep-picker"
      aria-label={`Obsadź: ${label}`}
      value=""
      autoFocus
      disabled={busy}
      onKeyDown={escape}
      onChange={(e) => {
        const person = people.find((x) => x.id === e.target.value);
        if (!person) return;
        if (r.place.id) fill.mutate({ placeId: r.place.id, personId: person.id, personName: person.name ?? 'Osoba', key: r.key });
        else fillRule.mutate({ personId: person.id, functionId: r.group.functionId, key: r.key });
      }}
    >
      <option value="">wybierz osobę…</option>
      {personOptions(
        people.filter((x) => mayTake(x, r.group)),
        unavailable,
        [r.day],
      )}
    </select>
  );

  const personPicker = (r: PanelRow, label: string) => (
    <select
      className="ep-picker"
      aria-label={`Zmień osobę: ${label}`}
      value=""
      autoFocus
      disabled={busy}
      onKeyDown={escape}
      onChange={(e) => {
        const person = people.find((x) => x.id === e.target.value);
        if (person) change.mutate({ place: r.place, version: r.assignment?.version, personId: person.id, personName: person.name ?? 'Osoba', key: r.key });
      }}
    >
      <option value="">zamiast: {r.name}</option>
      {personOptions(
        people.filter((x) => mayTake(x, r.group) && x.id !== r.personId),
        unavailable,
        [r.day],
      )}
    </select>
  );

  const statusPicker = (a: Assignment, key: string) => (
    <select
      className="ep-picker ep-status-picker"
      aria-label={`Status: ${a.personName ?? 'osoba'}${a.functionName ? ` (${a.functionName})` : ''}`}
      value={a.status}
      autoFocus
      disabled={changeStatus.isPending}
      onKeyDown={escape}
      // review M4: left without a choice — back to the word
      onBlur={() => {
        if (!changeStatus.isPending) setPicking(null);
      }}
      onChange={(e) => changeStatus.mutate({ a, status: e.target.value as AssignmentStatus, key })}
    >
      {(Object.keys(ASSIGNMENT_STATUSES) as AssignmentStatus[]).map((st) => (
        <option key={st} value={st}>
          {ASSIGNMENT_STATUSES[st]}
        </option>
      ))}
    </select>
  );

  const release = (r: PanelRow, view: PositionsView) => {
    const id = r.assignment!.id;
    const present = view.days.filter((d) => d.groups.some((g) => [...g.places, ...(g.extra ?? [])].some((x) => x.assignmentId === id))).map((d) => d.day);
    // review I1 (as in Obsada): their last day here — the release takes them out of the crew: ask first
    if (present.every((x) => x === r.day)) setLastDay({ assignmentId: id, day: r.day, personName: r.name });
    else releasePerson.mutate({ assignmentId: id, day: r.day });
  };

  const dialogs = (
    <>
      {selfAsk.dialog}
      {confirmFor && (
        <ConfirmManualDialog
          subject={`${confirmFor.name} · ${event.name || 'Wydarzenie'} · ${shortDate(confirmFor.day)}`}
          busy={confirmManually.isPending}
          error={confirmManually.error ? errorText(confirmManually.error, 'Nie udało się potwierdzić') : null}
          onConfirm={(note) => confirmManually.mutate({ a: confirmFor.a, note, key: confirmFor.key })}
          onClose={() => {
            setConfirmFor(null);
            confirmManually.reset();
          }}
        />
      )}
      {lastDay && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="ep-last-day-title">
            <DialogLayer onClose={() => setLastDay(null)} busy={releasePerson.isPending} />
            <h2 id="ep-last-day-title">Zdjąć osobę z ostatniego dnia?</h2>
            <p>
              {lastDay.personName} nie pracuje w innych dniach tego wydarzenia — po zdjęciu z miejsca ({dayShort(lastDay.day)}) zniknie z obsady. Miejsce zostanie wolne.
            </p>
            <div className="actions">
              <button
                className="btn primary"
                type="button"
                disabled={releasePerson.isPending}
                onClick={() => {
                  releasePerson.mutate({ assignmentId: lastDay.assignmentId, day: lastDay.day });
                  setLastDay(null);
                }}
              >
                Zdejmij i usuń z obsady
              </button>
              <button className="btn" type="button" onClick={() => setLastDay(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {askDays && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="ep-other-days-title">
            <DialogLayer onClose={() => setAskDays(null)} busy={fillOthers.isPending} />
            <h2 id="ep-other-days-title">Obsadzić tę osobę także w pozostałych dniach?</h2>
            <p>
              {askDays.personName} — {askDays.days.map(dayShort).join(', ')}. Dni, w których osoba jest niedostępna, zostaną pominięte; zajętość w innym wydarzeniu pokaże się jako kolizja.
            </p>
            {fillOthers.error && (
              <p className="alert" role="alert">
                {errorText(fillOthers.error, 'Nie udało się obsadzić')}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={fillOthers.isPending} onClick={() => fillOthers.mutate(askDays.positionId)}>
                Tak, wszystkie dni
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setAskDays(null);
                  onChanged();
                }}
              >
                Nie, tylko ten dzień
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return {
    picking,
    pick: (key: string, mode: PickMode) => {
      setRowError(null);
      setPicking({ key, mode });
    },
    picker,
    personPicker,
    statusPicker,
    rowError,
    release,
    force: (a: Assignment, day: string) => forceDay.mutate({ a, day }),
    confirmManually: (a: Assignment, name: string, day: string, key: string) => {
      confirmManually.reset();
      setRowError(null);
      setConfirmFor({ a, name, day, key });
    },
    notice,
    clearNotice: () => setNotice(null),
    error: error ? errorText(error, 'Nie udało się zmienić obsady') : null,
    dialogs,
  };
}

/**
 * Owner 2026-10-28 12:01: ⓘ beside „potwierdzony” — how it was confirmed: „Potwierdzone ręcznie przez … · date time” or
 * „Odpowiedział(a) na zapytanie · date time”, then the note; „brak danych” for older data. A tooltip describing ⓘ
 * (review 6): a click (or Enter / Space) keeps it open until a click again, a click outside, the focus leaving it or Esc
 * (only it closes; the focus stays on ⓘ). Hover shows it too, but then Esc is not its (the panel's) and no focus moves.
 */
function ConfirmInfo({ name, confirmation }: { name: string; confirmation: Confirmation | undefined }) {
  const [pinned, setPinned] = useState(false);
  const [hover, setHover] = useState(false);
  const open = pinned || hover;
  const button = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLSpanElement>(null);
  const id = useId();
  const close = () => {
    setPinned(false);
    setHover(false);
  };
  // only a tooltip opened on purpose takes Esc
  useEscapeLayer(() => {
    close();
    button.current?.focus();
  }, pinned);
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pinned]);
  return (
    <span
      ref={box}
      className="ep-confirm"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget as Node | null)) setPinned(false);
      }}
    >
      <button ref={button} type="button" className="ep-info" aria-label={`Jak potwierdzono: ${name}`} aria-expanded={open} aria-describedby={open ? id : undefined} onClick={() => (pinned ? close() : setPinned(true))}>
        ⓘ
      </button>
      {open && (
        <span id={id} className="ep-confirm-pop" role="tooltip">
          {confirmationLines(confirmation).map((line, i) => (
            <Fragment key={line}>
              {i > 0 && ' '}
              <span className="ep-confirm-line">{line}</span>
            </Fragment>
          ))}
        </span>
      )}
    </span>
  );
}
